import {
  CalendarDate,
  DateFormatter,
  getLocalTimeZone,
  ZonedDateTime
} from '@internationalized/date';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createFormatShortDate } from './createFormatShortDate';
import { FormatService } from './FormatService';
import { useFormatMoney } from './useFormatMoney';
import { useFormatShortDate } from './useFormatShortDate';
import { useFormatTimestamp } from './useFormatTimestamp';

export function createFormatService(): FormatService {
  return {
    useZonedDateTime() {
      const { i18n } = useTranslation();
      const locale = i18n.language;

      const formatter = useMemo(
        () =>
          new DateFormatter(locale, {
            dateStyle: 'short',
            timeStyle: 'short'
          }),
        [locale]
      );

      return {
        inLocalTime: (date: ZonedDateTime) => {
          const jsDate = date.toDate();
          return formatter.format(jsDate);
        }
      };
    },

    useCalendarDate() {
      const { i18n } = useTranslation();
      const locale = i18n.language;
      const timezone = getLocalTimeZone();
      const shortDate = useMemo(() => createFormatShortDate(locale), [locale]);

      return {
        full: (date: CalendarDate) => shortDate.format(date.toDate(timezone))
      };
    },

    useCurrency() {
      const { i18n } = useTranslation();
      const locale = i18n.language;

      const formatCurrencyFromWholeUnit = useMemo(
        () => (amount: number, currency: string) =>
          new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currency || 'USD'
          }).format(amount ?? 0),
        [locale]
      );

      const formatOptionalCurrencyWholeUnit = useMemo(
        () => (amount: number | null, currency: string) => {
          const code = currency || 'USD';
          if (amount == null || !Number.isFinite(amount)) {
            return new Intl.NumberFormat(locale, {
              style: 'currency',
              currency: code,
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }).format(0);
          }
          return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: code,
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
          }).format(amount);
        },
        [locale]
      );

      return {
        formatCurrencyFromWholeUnit,
        formatOptionalCurrencyWholeUnit
      };
    },

    useFormatMoney,
    useFormatShortDate,
    useFormatTimestamp
  };
}
